import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Phone, MapPin, ArrowRight, CheckCircle2 } from "lucide-react";

const ServiceAreas = () => {
  const mainAreas = [
    {
      name: "Atlanta",
      neighborhoods: ["Downtown", "Midtown", "Buckhead", "Virginia-Highland", "Inman Park", "Old Fourth Ward"],
    },
    {
      name: "Sandy Springs",
      neighborhoods: ["Riverside", "Perimeter Center", "Hammond Drive", "Spalding Drive"],
    },
    {
      name: "Marietta",
      neighborhoods: ["Historic Marietta", "East Cobb", "West Cobb", "Kennesaw"],
    },
    {
      name: "Decatur",
      neighborhoods: ["Downtown Decatur", "Oakhurst", "Winnona Park", "Medlock Park"],
    },
  ];

  const additionalAreas = [
    "Alpharetta",
    "Roswell",
    "Johns Creek",
    "Dunwoody",
    "Brookhaven",
    "Chamblee",
    "Tucker",
    "Stone Mountain",
    "Smyrna",
    "Vinings",
    "Peachtree City",
    "Lawrenceville",
    "Duluth",
    "Suwanee",
    "Norcross",
    "Lilburn",
  ];

  const zipCodes = [
    "30301", "30302", "30303", "30304", "30305", "30306", "30307", "30308",
    "30309", "30310", "30311", "30312", "30313", "30314", "30315", "30316",
    "30317", "30318", "30319", "30320", "30324", "30326", "30327", "30328",
    "30329", "30330", "30331", "30332", "30333", "30334", "30336", "30337",
    "30338", "30339", "30340", "30341", "30342", "30344", "30345", "30346",
  ];

  return (
    <div data-testid="service-areas-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-primary/5 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="text-sm font-bold text-primary uppercase tracking-wider">Service Areas</span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-secondary mt-4">
              Serving Greater Atlanta
            </h1>
            <p className="text-lg text-slate-600 mt-6">
              CartNexa proudly provides professional cleaning services throughout the Atlanta metropolitan area and surrounding communities.
            </p>
          </div>
        </div>
      </section>

      {/* Main Service Areas */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary">
              Primary Service Areas
            </h2>
            <p className="text-slate-600 mt-4">
              Same-day and next-day availability in these locations
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {mainAreas.map((area, index) => (
              <Card key={index} className="bg-slate-50 border-0 rounded-2xl" data-testid={`main-area-${index}`}>
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-heading text-2xl font-bold text-secondary">{area.name}</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {area.neighborhoods.map((neighborhood, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                        <span className="text-slate-600 text-sm">{neighborhood}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Areas */}
      <section className="py-20 md:py-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary">
              Additional Service Areas
            </h2>
            <p className="text-slate-600 mt-4">
              We also serve these communities in the greater Atlanta metro area
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {additionalAreas.map((area, index) => (
              <div
                key={index}
                className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm"
              >
                <MapPin className="w-4 h-4 text-primary" />
                <span className="text-slate-700 font-medium">{area}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ZIP Codes */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-secondary">
              ZIP Codes We Serve
            </h2>
            <p className="text-slate-600 mt-4">
              Not sure if we serve your area? Check your ZIP code below or give us a call.
            </p>
          </div>

          <Card className="bg-slate-50 border-0 rounded-2xl">
            <CardContent className="p-8">
              <div className="flex flex-wrap justify-center gap-2">
                {zipCodes.map((zip, index) => (
                  <span
                    key={index}
                    className="bg-white px-3 py-1 rounded-lg text-sm text-slate-600 font-mono"
                  >
                    {zip}
                  </span>
                ))}
              </div>
              <p className="text-center text-slate-500 mt-6 text-sm">
                Don't see your ZIP code? Contact us anyway—we may still be able to serve you!
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Coverage Info */}
      <section className="py-20 md:py-28 bg-primary/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-white border-0 rounded-2xl card-hover">
              <CardContent className="p-8 text-center">
                <div className="text-4xl font-bold text-primary mb-2">40+</div>
                <p className="text-slate-600">ZIP Codes Covered</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-0 rounded-2xl card-hover">
              <CardContent className="p-8 text-center">
                <div className="text-4xl font-bold text-primary mb-2">20+</div>
                <p className="text-slate-600">Cities & Towns</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-0 rounded-2xl card-hover">
              <CardContent className="p-8 text-center">
                <div className="text-4xl font-bold text-primary mb-2">50mi</div>
                <p className="text-slate-600">Service Radius</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Request a free quote and we'll confirm availability in your area.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/#quote-form">
              <Button className="h-14 px-8 bg-accent hover:bg-accent/90 text-white font-bold rounded-full text-lg btn-accent-hover" data-testid="service-areas-quote-btn">
                Get Free Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+18717871311">
              <Button variant="outline" className="h-14 px-8 border-2 border-white text-white font-bold rounded-full text-lg hover:bg-white hover:text-secondary">
                <Phone className="mr-2 w-5 h-5" />
                +1-871-7871311
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceAreas;
